// src/components/Cases.js
import React, { useEffect, useMemo, useState } from "react";
import api from "../utils/api";
import { useNavigate } from "react-router-dom";
import {
  FaSearch,
  FaTrash,
  FaEdit,
  FaEye,
  FaPlus,
  FaKey,
  FaCopy,
  FaTimes,
  FaBriefcase,
  FaUserTie,
  FaMoneyBillWave,
  FaCalendarAlt,
  FaFileAlt,
} from "react-icons/fa";
import toast, { Toaster } from "react-hot-toast";

function Cases() {
  const navigate = useNavigate();

  const [cases, setCases] = useState([]);
  const [total, setTotal] = useState(0);

  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");

  const [loading, setLoading] = useState(false);

  // ✅ Modal
  const [openAdd, setOpenAdd] = useState(false);
  const [adding, setAdding] = useState(false);

  const limit = 6;

  const initialForm = {
    title: "",
    type: "مدني",
    startDate: "",
    trialDate: "",
    price: "",
    notes: "",
    clientData: { name: "", phone: "", email: "", address: "" },
  };

  const [form, setForm] = useState(initialForm);

  const pagesCount = useMemo(() => {
    return Math.max(1, Math.ceil((total || 0) / limit));
  }, [total]);

  // ✅ Helpers
  const formatDateShort = (date) => {
    try {
      return new Date(date).toLocaleDateString("ar-EG");
    } catch {
      return "—";
    }
  };

  const safeNum = (v) => {
    const n = Number(v);
    return Number.isNaN(n) ? 0 : n;
  };

  const fetchCases = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/cases?page=${page}&limit=${limit}&q=${q}`);
      setCases(res.data.cases || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      toast.error("❌ فشل تحميل القضايا");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCases();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, q]);

  // ✅ Add Case
  const addCase = async () => {
    if (!form.title.trim() || !form.clientData.name.trim()) {
      return toast.error("⚠️ أدخل عنوان القضية واسم العميل");
    }
    if (!form.startDate) {
      return toast.error("⚠️ اختر تاريخ بداية القضية");
    }

    try {
      setAdding(true);

      const payload = {
        ...form,
        title: form.title.trim(),
        type: (form.type || "").trim(),
        notes: (form.notes || "").trim(),
        price: safeNum(form.price),
        clientData: {
          name: form.clientData.name.trim(),
          phone: (form.clientData.phone || "").trim(),
          email: (form.clientData.email || "").trim(), // ✅ رقم التوكيل
          address: (form.clientData.address || "").trim(),
        },
      };

      await api.post("/cases", payload);

      toast.success("✅ تم إضافة القضية بنجاح");
      setForm(initialForm);
      setOpenAdd(false);

      // ✅ رجع لأول صفحة عشان يشوفها فوق
      setPage(1);
      fetchCases();
    } catch (err) {
      toast.error(err.response?.data?.message || "❌ حدث خطأ أثناء الإضافة");
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("⚠️ هل تريد حذف هذه القضية؟")) return;

    try {
      await api.delete(`/cases/${id}`);
      toast.success("🗑️ تم حذف القضية");
      fetchCases();
    } catch (err) {
      toast.error(err.response?.data?.message || "❌ فشل الحذف");
    }
  };

  const handleEdit = async (id) => {
    const newTitle = prompt("أدخل العنوان الجديد:");
    if (!newTitle || !newTitle.trim()) return;

    try {
      await api.put(`/cases/${id}`, { title: newTitle.trim() });
      toast.success("✏️ تم التعديل");
      fetchCases();
    } catch (err) {
      toast.error(err.response?.data?.message || "❌ فشل التعديل");
    }
  };

  const copyCaseCode = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      toast.success("📋 تم نسخ كود القضية");
    } catch {
      toast.error("❌ فشل النسخ");
    }
  };

  // ✅ Nice empty state
  const EmptyState = () => (
    <div className="bg-gray-900 border border-gray-800 rounded-3xl p-6 text-center">
      <p className="text-2xl mb-2">📭</p>
      <p className="text-gray-200 font-extrabold">لا توجد قضايا</p>
      <p className="text-gray-400 text-sm mt-2">
        ابدأ بإضافة قضية جديدة لتظهر هنا.
      </p>

      <button
        onClick={() => setOpenAdd(true)}
        className="mt-5 w-full bg-yellow-500 hover:bg-yellow-600 text-black font-extrabold px-5 py-3 rounded-2xl"
      >
        <span className="inline-flex items-center justify-center gap-2">
          <FaPlus /> إضافة قضية
        </span>
      </button>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-gray-950 to-black text-white px-4 pb-24">
      <Toaster position="top-center" />

      {/* Header */}
      <div className="pt-6 pb-5">
        <div className="max-w-5xl mx-auto">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-2xl sm:text-3xl font-black text-yellow-400 drop-shadow">
                ⚖️ إدارة القضايا
              </h2>
              <p className="text-gray-400 text-sm mt-1">
                إدارة القضايا + المواعيد + كود دخول المحامي المساعد
              </p>
            </div>

            <button
              onClick={() => setOpenAdd(true)}
              className="w-full sm:w-auto bg-yellow-500 hover:bg-yellow-600 text-black font-extrabold px-5 py-3 rounded-2xl shadow-lg"
            >
              <span className="inline-flex items-center justify-center gap-2">
                <FaPlus /> إضافة قضية جديدة
              </span>
            </button>
          </div>

          {/* Search */}
          <div className="mt-5 bg-gray-900 border border-gray-800 rounded-3xl p-3 sm:p-4 flex items-center gap-3 shadow-xl">
            <div className="w-10 h-10 rounded-2xl bg-yellow-500/15 flex items-center justify-center border border-yellow-500/30">
              <FaSearch className="text-yellow-400" />
            </div>

            <input
              type="text"
              placeholder="ابحث بعنوان القضية..."
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                setPage(1);
              }}
              className="flex-1 bg-transparent outline-none text-white placeholder-gray-500 text-base"
            />

            {q ? (
              <button
                onClick={() => {
                  setQ("");
                  setPage(1);
                }}
                className="text-gray-300 hover:text-white px-3 py-2 rounded-xl bg-gray-800 border border-gray-700"
              >
                مسح
              </button>
            ) : null}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto">
        {loading ? (
          <div className="text-center text-yellow-400 font-bold mt-20 animate-pulse">
            ⏳ جاري تحميل القضايا...
          </div>
        ) : cases.length === 0 ? (
          <EmptyState />
        ) : (
          <>
            {/* Stats strip */}
            <div className="mb-6 flex flex-col sm:flex-row gap-3">
              <div className="bg-gray-900 border border-gray-800 rounded-3xl p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-2xl bg-yellow-500/15 flex items-center justify-center border border-yellow-500/30">
                  <FaBriefcase className="text-yellow-400" />
                </div>
                <div>
                  <p className="text-gray-400 text-xs">إجمالي القضايا</p>
                  <p className="font-black text-lg">{total}</p>
                </div>
              </div>

              <div className="bg-gray-900 border border-gray-800 rounded-3xl p-4 flex items-center gap-3 flex-1">
                <div className="w-10 h-10 rounded-2xl bg-green-500/15 flex items-center justify-center border border-green-500/30">
                  <FaCalendarAlt className="text-green-400" />
                </div>
                <div>
                  <p className="text-gray-400 text-xs">نصيحة</p>
                  <p className="text-gray-200 text-sm font-bold">
                    افتح أي قضية لإضافة جلسات مرتبطة بها (Timeline).
                  </p>
                </div>
              </div>
            </div>

            {/* Cards */}
            <div className="grid gap-4 sm:gap-6 md:grid-cols-2 lg:grid-cols-3">
              {cases.map((c) => (
                <div
                  key={c._id}
                  className="bg-gray-900 border border-gray-800 rounded-3xl p-5 shadow-xl hover:border-yellow-500/50 transition"
                >
                  {/* Title */}
                  <div className="flex items-start justify-between gap-3">
                    <h3 className="text-xl font-black text-yellow-400 leading-tight break-words">
                      {c.title}
                    </h3>

                    <button
                      onClick={() => navigate(`/cases/${c._id}`)}
                      className="shrink-0 bg-yellow-500 hover:bg-yellow-600 text-black px-3 py-2 rounded-2xl font-black"
                      title="فتح القضية"
                    >
                      <FaEye />
                    </button>
                  </div>

                  {/* Meta */}
                  <div className="mt-4 space-y-2 text-sm">
                    <p className="text-gray-200 flex items-center gap-2 break-words">
                      <FaUserTie className="text-yellow-400" />
                      <span className="text-gray-400">العميل:</span>
                      <span className="font-bold">{c.client?.name || "—"}</span>
                    </p>

                    <p className="text-gray-200 flex items-center gap-2 break-words">
                      <FaFileAlt className="text-yellow-400" />
                      <span className="text-gray-400">النوع:</span>
                      <span className="font-bold">{c.type || "—"}</span>
                    </p>

                    <p className="text-gray-200 flex items-center gap-2">
                      <FaCalendarAlt className="text-yellow-400" />
                      <span className="text-gray-400">التواريخ:</span>
                      <span className="font-bold">
                        {formatDateShort(c.startDate)}
                      </span>
                      <span className="text-gray-500">→</span>
                      <span className="font-bold">
                        {c.trialDate ? formatDateShort(c.trialDate) : "—"}
                      </span>
                    </p>

                    <p className="text-gray-200 flex items-center gap-2">
                      <FaMoneyBillWave className="text-yellow-400" />
                      <span className="text-gray-400">القيمة:</span>
                      <span className="font-black text-green-400">
                        {c.price || 0} ج.م
                      </span>
                    </p>
                  </div>

                  {/* Case Code */}
                  <div className="mt-4 bg-black/40 border border-gray-800 rounded-2xl p-3 flex items-center justify-between gap-3">
                    <div className="min-w-0 flex items-center gap-2">
                      <FaKey className="text-yellow-400" />
                      <span className="text-gray-300 text-sm font-bold truncate">
                        {c.caseCode}
                      </span>
                    </div>

                    <button
                      onClick={() => copyCaseCode(c.caseCode)}
                      className="bg-yellow-500/15 hover:bg-yellow-500/25 text-yellow-300 px-3 py-2 rounded-2xl border border-yellow-500/30"
                      title="نسخ"
                    >
                      <FaCopy />
                    </button>
                  </div>

                  {/* Actions */}
                  <div className="mt-4 grid grid-cols-3 gap-2">
                    <button
                      onClick={() => navigate(`/cases/${c._id}`)}
                      className="bg-yellow-500 hover:bg-yellow-600 text-black font-extrabold py-3 rounded-2xl"
                      title="عرض"
                    >
                      <FaEye className="mx-auto" />
                    </button>

                    <button
                      onClick={() => handleEdit(c._id)}
                      className="bg-blue-600 hover:bg-blue-700 text-white font-extrabold py-3 rounded-2xl"
                      title="تعديل"
                    >
                      <FaEdit className="mx-auto" />
                    </button>

                    <button
                      onClick={() => handleDelete(c._id)}
                      className="bg-red-600 hover:bg-red-700 text-white font-extrabold py-3 rounded-2xl"
                      title="حذف"
                    >
                      <FaTrash className="mx-auto" />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {pagesCount > 1 && (
              <div className="mt-10 flex flex-col items-center gap-4">
                <p className="text-gray-400 text-sm">
                  صفحة {page} من {pagesCount}
                </p>

                <div className="flex flex-wrap justify-center gap-2">
                  {Array.from({ length: pagesCount }).map((_, i) => {
                    const p = i + 1;
                    const active = p === page;

                    return (
                      <button
                        key={p}
                        onClick={() => setPage(p)}
                        className={`px-4 py-2 rounded-2xl text-sm font-extrabold border transition ${
                          active
                            ? "bg-yellow-500 text-black border-yellow-400"
                            : "bg-gray-900 text-gray-200 border-gray-800 hover:border-yellow-500/40"
                        }`}
                      >
                        {p}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* ✅ ADD CASE MODAL */}
      {openAdd && (
        <div className="fixed inset-0 bg-black/80 flex items-end sm:items-center justify-center p-3 z-50">
          <div className="w-full max-w-2xl bg-gray-950 border border-gray-800 rounded-t-3xl sm:rounded-3xl overflow-hidden shadow-2xl">
            {/* Modal header */}
            <div className="p-5 border-b border-gray-800 flex items-center justify-between">
              <div>
                <h3 className="text-xl font-black text-yellow-400">
                  ➕ إضافة قضية جديدة
                </h3>
                <p className="text-gray-400 text-sm mt-1">
                  املأ البيانات المطلوبة لإضافة القضية
                </p>
              </div>

              <button
                onClick={() => setOpenAdd(false)}
                className="bg-red-600 hover:bg-red-700 p-2 rounded-2xl"
                title="إغلاق"
              >
                <FaTimes />
              </button>
            </div>

            {/* Modal content */}
            <div className="p-5 max-h-[75vh] overflow-auto">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input
                  className="p-4 rounded-2xl bg-gray-900 text-white border border-gray-800 outline-none focus:border-yellow-500"
                  placeholder="عنوان القضية *"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                />

                <input
                  className="p-4 rounded-2xl bg-gray-900 text-white border border-gray-800 outline-none focus:border-yellow-500"
                  placeholder="نوع القضية (مثال: مدني / جنائي)"
                  value={form.type}
                  onChange={(e) => setForm({ ...form, type: e.target.value })}
                />

                <div className="sm:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="bg-gray-900 border border-gray-800 rounded-2xl p-3">
                    <p className="text-gray-400 text-xs mb-2">📅 تاريخ بداية القضية *</p>
                    <input
                      type="date"
                      className="w-full bg-transparent outline-none text-white"
                      value={form.startDate}
                      onChange={(e) => setForm({ ...form, startDate: e.target.value })}
                    />
                  </div>

                  <div className="bg-gray-900 border border-gray-800 rounded-2xl p-3">
                    <p className="text-gray-400 text-xs mb-2">📌 تاريخ أول جلسة (اختياري)</p>
                    <input
                      type="date"
                      className="w-full bg-transparent outline-none text-white"
                      value={form.trialDate}
                      onChange={(e) => setForm({ ...form, trialDate: e.target.value })}
                    />
                  </div>
                </div>

                <input
                  type="number"
                  placeholder="القيمة (ج.م)"
                  className="p-4 rounded-2xl bg-gray-900 text-white border border-gray-800 outline-none focus:border-yellow-500 sm:col-span-2"
                  value={form.price}
                  onChange={(e) => setForm({ ...form, price: e.target.value })}
                />

                <textarea
                  placeholder="ملاحظات (اختياري)"
                  className="p-4 rounded-2xl bg-gray-900 text-white border border-gray-800 outline-none focus:border-yellow-500 sm:col-span-2 min-h-[110px]"
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                />

                {/* Client Section */}
                <div className="sm:col-span-2 mt-2">
                  <div className="bg-gray-900 border border-gray-800 rounded-3xl p-4">
                    <h4 className="text-yellow-300 font-black mb-3">
                      👤 بيانات العميل
                    </h4>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <input
                        className="p-4 rounded-2xl bg-gray-950 text-white border border-gray-800 outline-none focus:border-yellow-500"
                        placeholder="اسم العميل *"
                        value={form.clientData.name}
                        onChange={(e) =>
                          setForm({
                            ...form,
                            clientData: { ...form.clientData, name: e.target.value },
                          })
                        }
                      />

                      <input
                        className="p-4 rounded-2xl bg-gray-950 text-white border border-gray-800 outline-none focus:border-yellow-500"
                        placeholder="رقم الهاتف"
                        value={form.clientData.phone}
                        onChange={(e) =>
                          setForm({
                            ...form,
                            clientData: { ...form.clientData, phone: e.target.value },
                          })
                        }
                      />

                      <input
                        className="p-4 rounded-2xl bg-gray-950 text-white border border-gray-800 outline-none focus:border-yellow-500 sm:col-span-2"
                        placeholder="رقم التوكيل"
                        value={form.clientData.email}
                        onChange={(e) =>
                          setForm({
                            ...form,
                            clientData: { ...form.clientData, email: e.target.value },
                          })
                        }
                      />

                      <input
                        className="p-4 rounded-2xl bg-gray-950 text-white border border-gray-800 outline-none focus:border-yellow-500 sm:col-span-2"
                        placeholder="العنوان"
                        value={form.clientData.address}
                        onChange={(e) =>
                          setForm({
                            ...form,
                            clientData: { ...form.clientData, address: e.target.value },
                          })
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer Buttons */}
              <div className="mt-5 flex flex-col sm:flex-row gap-3">
                <button
                  onClick={addCase}
                  disabled={adding}
                  className="w-full bg-yellow-500 hover:bg-yellow-600 text-black font-black px-5 py-4 rounded-2xl disabled:opacity-70"
                >
                  {adding ? "⏳ جاري الإضافة..." : "✅ إضافة القضية"}
                </button>

                <button
                  onClick={() => {
                    setForm(initialForm);
                    setOpenAdd(false);
                  }}
                  className="w-full bg-gray-800 hover:bg-gray-700 text-white font-black px-5 py-4 rounded-2xl border border-gray-700"
                >
                  إلغاء
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Cases;
